import { useState, type ReactNode } from "react";
import { AlertCircle, Hash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/PageHeader";
import { UtmResult } from "@/components/utm/UtmResult";
import { UtmHistory } from "@/components/utm/UtmHistory";
import { useTenant } from "@/lib/hooks/useTenant";
import { useSaveUtmLink } from "@/lib/hooks/useUtmLinks";
import { buildUtmUrl, EMPTY_UTM_FIELDS, UTM_PARAM_KEYS, type UtmFields } from "@/lib/utm";

const FIELD_LABELS: Record<string, string> = {
  url: "URL base",
  source: "utm_source *",
  medium: "utm_medium *",
  campaign: "utm_campaign *",
  content: "utm_content",
  term: "utm_term",
};

const FIELD_PLACEHOLDERS: Record<string, string> = {
  url: "https://exemplo.com/produto",
  source: "newsletter",
  medium: "email",
  campaign: "promo_junho",
  content: "banner_hero",
  term: "palavra_chave",
};

export function UtmPage(): JSX.Element {
  const [fields, setFields] = useState<UtmFields>(EMPTY_UTM_FIELDS);
  const { data: tenantBilling } = useTenant();
  const saveUtm = useSaveUtmLink();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (key: keyof UtmFields, value: string): void => {
    setFields((prev) => ({ ...prev, [key]: value }));
    setGeneratedUrl(null);
    setCopied(false);
    setError(null);
  };

  const handleGenerate = async (): Promise<void> => {
    const result = buildUtmUrl(fields);
    setCopied(false);
    setSaveError(null);
    if (!result.ok) {
      setGeneratedUrl(null);
      setError(result.error);
      return;
    }
    setGeneratedUrl(result.url);
    setError(null);
    const tenantId = tenantBilling?.tenant?.id;
    if (!tenantId) {
      setSaveError("URL gerada, mas não foi possível salvar no histórico (conta não carregada).");
      return;
    }
    try {
      await saveUtm.mutateAsync({ tenantId, fields, fullUrl: result.url });
    } catch {
      setSaveError("URL gerada, mas não foi possível salvar no histórico agora.");
    }
  };

  const handleCopy = async (): Promise<void> => {
    if (!generatedUrl) return;
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
    } catch {
      setError("Não foi possível copiar. Selecione a URL e copie manualmente.");
    }
  };

  const handleClear = (): void => {
    setFields(EMPTY_UTM_FIELDS);
    setGeneratedUrl(null);
    setCopied(false);
    setError(null);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <PageHeader
        title="Gerador de UTM"
        subtitle="Crie URLs com parâmetros UTM, gere o QR Code e consulte as campanhas já criadas."
      />

      <section
        className="rounded-xl border border-border bg-card overflow-hidden"
        aria-labelledby="section-utm-form"
      >
        <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border bg-[hsl(var(--surface-raised)/0.4)]">
          <Hash size={14} className="text-muted-foreground" />
          <h2
            id="section-utm-form"
            className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
          >
            Parâmetros UTM
          </h2>
        </div>

        <div className="p-5 space-y-5">
          {/* URL base */}
          <div className="space-y-2">
            <Label htmlFor="utm-url" variant="field">
              {FIELD_LABELS.url}
            </Label>
            <Input
              id="utm-url"
              value={fields.url}
              onChange={(e) => handleChange("url", e.target.value)}
              placeholder={FIELD_PLACEHOLDERS.url}
              autoComplete="off"
            />
          </div>

          {/* Grid dos parametros UTM */}
          <div className="grid gap-3 sm:grid-cols-2">
            {UTM_PARAM_KEYS.map((key) => (
              <div key={key} className="space-y-2">
                <Label
                  htmlFor={`utm-${key}`}
                  variant="field"
                  className="font-mono text-xs"
                >
                  {FIELD_LABELS[key]}
                </Label>
                <Input
                  id={`utm-${key}`}
                  value={fields[key]}
                  onChange={(e) => handleChange(key, e.target.value)}
                  placeholder={FIELD_PLACEHOLDERS[key]}
                  autoComplete="off"
                />
              </div>
            ))}
          </div>

          {/* Acoes */}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="gold"
              onClick={handleGenerate}
              disabled={!fields.url.trim()}
            >
              <Hash size={15} />
              Gerar URL
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleClear}
            >
              Limpar
            </Button>
          </div>

          {/* Erro */}
          {error && (
            <InlineNotice tone="danger" icon={<AlertCircle size={15} />}>
              {error}
            </InlineNotice>
          )}
        </div>
      </section>

      {generatedUrl && (
        <UtmResult url={generatedUrl} copied={copied} saveError={saveError} onCopy={() => void handleCopy()} />
      )}

      <UtmHistory />
    </div>
  );
}

// ---------------------------------------------------------------------------
// InlineNotice
// ---------------------------------------------------------------------------

function InlineNotice({
  children,
  icon,
  tone,
}: {
  children: ReactNode;
  icon: ReactNode;
  tone: "success" | "danger";
}): JSX.Element {
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm",
        tone === "success"
          ? "border-primary/25 bg-primary/10 text-primary"
          : "border-destructive/30 bg-destructive/10 text-destructive",
      )}
    >
      {icon}
      <span>{children}</span>
    </div>
  );
}
